import * as Api from '@ViewModels';
import { IAutomationTemplate, EmailMessageViewModel, ITemplate } from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { baseStyleSheet } from '../../../styles/styles';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../Select';
import { AutomationsIcon } from '../../svgs/icons/AutomationsIcon';
import { getTemplateApprovalStatus } from '../../TemplateReviewStatusIndicator';
import { styleSheet } from './styles';

interface IProps {
	alternateKeyDateTemplates?: ITemplate[];
	automationTemplates?: IAutomationTemplate[];
	emailMessage?: EmailMessageViewModel<File>;
	initialEmailBodyTemplate?: ITemplate;
	onSetTemplate?(template?: ITemplate | IAutomationTemplate): void;
	selectedTemplate?: ITemplate | IAutomationTemplate;
}

const AlternateKeyDatesHeaderItem: React.FC<{
	template?: ITemplate | IAutomationTemplate;
	emailMessage?: EmailMessageViewModel<File>;
}> = observer(({ template, emailMessage }) => {
	// Check if template is an automation template. Handles:
	// 1. IAutomationTemplateReference - has _type containing "Automation"
	// 2. IAutomationTemplate - has templateType === TemplateType.Automation
	// 3. AutomationTemplateViewModel (plugin) - has mModel with templateType
	const isAutomationTemplate = (() => {
		if (!template) {
			return false;
		}

		const templateAny = template as any;

		// Check _type for automation-related types (e.g., "AutomationTemplateReference", "AutomationTemplate")
		if (templateAny._type && typeof templateAny._type === 'string' && templateAny._type.includes('Automation')) {
			return true;
		}

		// Check if templateType is directly accessible (new ViewModel or raw interface).
		if ('templateType' in template && templateAny.templateType === Api.TemplateType.Automation) {
			return true;
		}

		// Check if it's a legacy ViewModel with mModel (plugin ViewModel pattern).
		if ('mModel' in template && templateAny.mModel?.templateType === Api.TemplateType.Automation) {
			return true;
		}

		return false;
	})();
	return isAutomationTemplate ? (
		<div
			className={css(
				baseStyleSheet.truncateText,
				baseStyleSheet.horizontalStack,
				styleSheet.automationTemplateSelectorItem
			)}
		>
			<div className={css(styleSheet.automationTemplateSelectorAutomationIcon)}>
				<AutomationsIcon fillColor='#fff' />
			</div>
			<div className={css(baseStyleSheet.truncateText)}>{template.name}</div>
			{(template as any)?.templateReview && (
				<div className={css(styleSheet.approvalStatusContainer)}>
					{getTemplateApprovalStatus((template as any).templateReview)}
				</div>
			)}
		</div>
	) : (
		<div className={css(baseStyleSheet.truncateText, baseStyleSheet.horizontalStack)}>
			<div className={css(baseStyleSheet.truncateText)}>
				<div className={css(baseStyleSheet.truncateText)}>{template?.name || emailMessage?.subject}</div>
			</div>
			{template?.templateReview && (
				<div className={css(styleSheet.approvalStatusContainer)}>
					{getTemplateApprovalStatus(template.templateReview)}
				</div>
			)}
		</div>
	);
});

export const AlternateKeyDatesHeader: React.FC<IProps> = observer(props => {
	const {
		alternateKeyDateTemplates = [],
		automationTemplates = [],
		emailMessage,
		initialEmailBodyTemplate,
		onSetTemplate,
		selectedTemplate,
	} = props;

	const templateChoices = [...automationTemplates, ...alternateKeyDateTemplates, initialEmailBodyTemplate].filter(
		x => !!x
	);

	return (
		<Select
			value={selectedTemplate?.id}
			onValueChange={templateId => {
				const template = templateChoices?.find(t => templateId === t.id);
				if (selectedTemplate?.id !== template.id) {
					onSetTemplate(template);
				}
			}}
		>
			<SelectTrigger>
				<SelectValue
					render={() => <AlternateKeyDatesHeaderItem template={selectedTemplate} emailMessage={emailMessage} />}
				/>
			</SelectTrigger>
			<SelectContent>
				{templateChoices?.map(x => {
					return (
						<SelectItem value={x.id} key={`alternateKeyDatesHeader-${x.id}`}>
							<AlternateKeyDatesHeaderItem template={x} />
						</SelectItem>
					);
				})}
			</SelectContent>
		</Select>
	);
});
